import React from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import googleIcon from "../../../assets/images/google.svg";
import facebookIcon from "../../../assets/images/facebook.svg";
import type {
  FlowComponentRenderProps,
  ReviewSelectionComponentConfig,
} from "../../types";
import HtmlText from "../HtmlText";
import { reviewSiteMeta } from "./reviewSiteMeta";
import { useCompanyBrandColorScheme } from "../../CompanyBrandProvider";

/** Figma defaults for the Direct Review prompt card (U+2019 apostrophe, unspaced U+2014 em dash). */
const RECOGNIZE_TITLE = "Want us to recognize your tech?";
const RECOGNIZE_BODY =
  "Leave a Google review, and we\u2019ll automatically match it to your service so they get credit\u2014no need to mention their name.";
const BONUS_BODY =
  "Leave a Google review, and we\u2019ll automatically match it to your service and send them the bonus\u2014no need to mention their name.";

/** Module / legacy titles that should yield to Figma prompt defaults. */
const PLACEHOLDER_TITLES = new Set([
  "",
  "Choose where to post",
  "Let us tip your tech",
  "Share your feedback publicly.",
]);

function isPlaceholderTitle(title: string) {
  if (PLACEHOLDER_TITLES.has(title)) return true;
  // Rate-config / admin defaults historically looked like "Let us tip your tech $5".
  return /^Let us tip your tech\b/i.test(title);
}

/** Rate-config schema/form defaults that should yield to Figma body copy. */
function isPlaceholderBonusMessage(message: string) {
  // Schema: "...{{TipAmount}}..." and form defaults with a literal $ amount.
  return /^Write a positive review about your tech\b/i.test(message);
}

function reviewButtonSx(
  config: ReviewSelectionComponentConfig,
  selected: boolean,
) {
  const filled = (config.buttonStyle ?? "filled") === "filled";
  const borderColor = selected
    ? "var(--cx-brand-primary-dark)"
    : filled
      ? "var(--cx-brand-button-selected-border)"
      : "var(--cx-brand-button-border)";

  return {
    bgcolor: filled
      ? selected
        ? "var(--cx-brand-primary-dark)"
        : "var(--cx-brand-button-selected-bg)"
      : "var(--cx-brand-button-bg)",
    border: "1px solid",
    borderColor,
    borderRadius: `${config.buttonBorderRadius ?? 8}px`,
    color: filled
      ? "var(--cx-brand-button-selected-text)"
      : "var(--cx-brand-button-text)",
    gap: 1,
    justifyContent: "center",
    minHeight: config.buttonMinHeight ?? 48,
    px: 2,
    py: 1,
    width: "100%",
    "&:hover": {
      bgcolor: filled
        ? "var(--cx-brand-primary-dark)"
        : "var(--cx-brand-button-hover-bg)",
      borderColor: filled
        ? "var(--cx-brand-primary-dark)"
        : "var(--cx-brand-primary)",
    },
  } as const;
}

function bonusDefaultTitle(amount: number) {
  return `Want us to send your tech a $${amount} bonus?`;
}

function ProviderIcon({
  label,
  provider,
}: {
  label: string;
  provider: string;
}) {
  if (provider === "google") {
    return (
      <Box
        alt="Google"
        component="img"
        src={googleIcon}
        sx={{ height: 24, width: 24 }}
      />
    );
  }

  if (provider === "facebook") {
    return (
      <Box
        alt="Facebook"
        component="img"
        src={facebookIcon}
        sx={{ height: 24, width: 24 }}
      />
    );
  }

  if (provider === "yelp") {
    return (
      <Box
        aria-label="Yelp"
        component="span"
        sx={{ color: "#D32323", fontSize: 28, lineHeight: 1 }}
      >
        ✱
      </Box>
    );
  }

  const meta = reviewSiteMeta(provider, label);
  return (
    <Box
      component="span"
      sx={{
        color: meta.color,
        fontSize: provider === "bbb" ? 11 : 22,
        fontWeight: 900,
      }}
    >
      {meta.mark}
    </Box>
  );
}

/**
 * Direct-review prompt card — Figma nodes 2583:75575 / 2583:75990.
 * Screen chrome (header/subheading) stays left-aligned; this card is center-aligned.
 */
function ReviewPromptCard({
  body,
  config,
  onSelectSite,
  selectedSiteId,
  sites,
  title,
}: {
  body: string;
  config: ReviewSelectionComponentConfig;
  onSelectSite: (site: ReviewSelectionComponentConfig["sites"][number]) => void;
  selectedSiteId?: string;
  sites: ReviewSelectionComponentConfig["sites"];
  title: string;
}) {
  const dark = useCompanyBrandColorScheme() === "dark";
  return (
    <Box
      sx={{
        bgcolor: dark ? "transparent" : "#F4F6F8",
        border: dark ? 0 : "1px solid rgba(145, 158, 171, 0.20)",
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        overflow: "hidden",
        pb: dark ? 0 : 1.5,
        pt: dark ? 0 : 1.25,
        px: dark ? 0 : 1.5,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          mx: "auto",
          maxWidth: 310,
          textAlign: "center",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: "var(--cx-brand-text)",
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "24px",
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "var(--cx-brand-text-muted)",
            fontSize: 13,
            lineHeight: "20px",
            textAlign: "center",
          }}
        >
          {body}
        </Typography>
      </Box>

      <Stack spacing={0.75} sx={{ width: "100%" }}>
        {sites.map((site) => {
          const selected = selectedSiteId === site.id;
          const providerId = site.provider ?? site.id;

          return (
            <Button
              color="inherit"
              key={site.id}
              onClick={() => onSelectSite(site)}
              sx={reviewButtonSx(config, selected)}
              type="button"
              variant={
                config.buttonStyle === "outlined" ? "outlined" : "contained"
              }
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexShrink: 0,
                  height: 24,
                  justifyContent: "center",
                  width: 24,
                }}
              >
                <ProviderIcon label={site.label} provider={providerId} />
              </Box>
              <Typography
                sx={{
                  color: "inherit",
                  fontSize: 15,
                  fontWeight: 700,
                  lineHeight: "26px",
                }}
              >
                {reviewButtonText(config, site.label)}
              </Typography>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

function resolvePromptTitle(
  config: ReviewSelectionComponentConfig,
  isBonus: boolean,
  bonusAmount: number,
) {
  const raw = (config.title || "").trim();
  if (raw && !isPlaceholderTitle(raw)) {
    return raw;
  }
  return isBonus ? bonusDefaultTitle(bonusAmount) : RECOGNIZE_TITLE;
}

function resolvePromptBody(
  config: ReviewSelectionComponentConfig,
  isBonus: boolean,
) {
  const configuredBody = (config.reviewPromptBody || "").trim();
  if (configuredBody) {
    return configuredBody;
  }
  const raw = (config.bonusMessage || "").trim();
  if (raw && !isPlaceholderBonusMessage(raw)) {
    return raw;
  }
  return isBonus ? BONUS_BODY : RECOGNIZE_BODY;
}

function reviewButtonText(
  config: ReviewSelectionComponentConfig,
  label: string,
) {
  const template = (config.reviewButtonLabel || "").trim();
  return template
    ? template.replace(/\{provider\}/gi, label)
    : `Write a Review on ${label}`;
}

const ReviewSelectionBlock: React.FC<
  FlowComponentRenderProps<ReviewSelectionComponentConfig>
> = ({ config, goToScreen, onAction, onInteract, setValue, value }) => {
  const selectedSiteId = typeof value === "string" ? value : undefined;
  const isBonusPrompt = config.variant === "no_nps_v3";
  const isDirectReviewPrompt =
    isBonusPrompt || config.showFeedbackPreview !== false;
  const bonusAmount = config.bonusAmount ?? 5;

  const selectSite = (
    site: ReviewSelectionComponentConfig["sites"][number],
  ) => {
    if (!onInteract) setValue(config.id, site.id);
    (onInteract ?? onAction)?.({
      componentId: config.id,
      label: site.label,
      metadata: {
        destinationSource: site.destinationSource,
        locationLabel: site.locationLabel,
        provider: site.provider,
        strategy: site.strategy,
        weight: site.weight,
      },
      type: "review",
      url: site.url,
      value: site.id,
    });
  };

  return (
    <Card
      sx={{
        bgcolor: "transparent",
        border: 0,
        borderRadius: 0,
        boxShadow: "none",
        color: "var(--cx-brand-text)",
        p: 0,
      }}
    >
      <Stack spacing={2}>
        {isDirectReviewPrompt ? (
          <ReviewPromptCard
            body={resolvePromptBody(config, isBonusPrompt)}
            config={config}
            onSelectSite={selectSite}
            selectedSiteId={selectedSiteId}
            sites={config.sites}
            title={resolvePromptTitle(config, isBonusPrompt, bonusAmount)}
          />
        ) : (
          <>
            {config.title ? (
              <HtmlText
                component="div"
                html={config.title}
                variant="subtitle2"
                sx={{ textAlign: "center" }}
              />
            ) : null}

            <Stack spacing={0.75}>
              {config.sites.map((site) => {
                const selected = selectedSiteId === site.id;
                const providerId = site.provider ?? site.id;

                return (
                  <Button
                    color="inherit"
                    key={site.id}
                    onClick={() => selectSite(site)}
                    sx={reviewButtonSx(config, selected)}
                    type="button"
                    variant={
                      config.buttonStyle === "outlined"
                        ? "outlined"
                        : "contained"
                    }
                  >
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        flexShrink: 0,
                        height: 24,
                        justifyContent: "center",
                        width: 24,
                      }}
                    >
                      <ProviderIcon label={site.label} provider={providerId} />
                    </Box>
                    <Typography
                      sx={{
                        color: "inherit",
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: "22px",
                      }}
                    >
                      {reviewButtonText(config, site.label)}
                    </Typography>
                  </Button>
                );
              })}
            </Stack>
          </>
        )}

        {config.callbackLabel ? (
          <ButtonBase
            onClick={() =>
              (onInteract ?? onAction)?.({
                componentId: config.id,
                label: config.callbackLabel,
                phone: config.callbackPhone,
                type: "callback",
                value: true,
              })
            }
            sx={{
              color: "var(--cx-brand-text-muted)",
              fontSize: 14,
              lineHeight: "22px",
              mx: "auto",
            }}
            type="button"
          >
            Prefer to talk to someone?&nbsp;
            <Box
              component="span"
              sx={{ color: "var(--cx-brand-text)", fontWeight: 600 }}
            >
              {config.callbackLabel}
            </Box>
          </ButtonBase>
        ) : null}

        {config.tipTargetScreenId ? (
          <ButtonBase
            onClick={() => {
              if (onInteract) {
                onInteract({ componentId: config.id, label: "Leave a tip", metadata: { technicianName: config.technicianName }, type: "tip", value: true });
                return;
              }
              onAction?.({
                componentId: config.id,
                label: "Leave a tip",
                metadata: { technicianName: config.technicianName },
                type: "tip",
                value: true,
              });
              goToScreen(config.tipTargetScreenId!);
            }}
            sx={{
              alignItems: "center",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 0.75,
              display: "flex",
              minHeight: 60,
              mt: "16px !important",
              px: 1.5,
              py: 1,
              textAlign: "left",
              width: "100%",
              "&:hover": { bgcolor: "grey.200" },
            }}
            type="button"
          >
            <Box
              aria-hidden="true"
              sx={{
                alignItems: "center",
                background:
                  "radial-gradient(circle at 35% 30%, #FFE16A 0 18%, #F3BB16 19% 63%, #D99A00 64% 100%)",
                border: "2px solid #FFC928",
                borderRadius: "50%",
                boxShadow: "inset 0 0 0 3px rgba(255,255,255,0.28)",
                color: "#A66B00",
                display: "flex",
                flexShrink: 0,
                fontSize: 25,
                fontWeight: 900,
                height: 44,
                justifyContent: "center",
                mr: 1.25,
                width: 44,
              }}
            >
              $
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, lineHeight: "22px" }}
              >
                Enjoyed the service?
              </Typography>
              <Typography
                sx={{
                  color: "var(--cx-brand-text-muted)",
                  fontSize: 14,
                  lineHeight: "22px",
                }}
              >
                Leave {config.technicianName ?? "your technician"} a tip
              </Typography>
            </Box>
            <ChevronRightRoundedIcon sx={{ fontSize: 26 }} />
          </ButtonBase>
        ) : null}
      </Stack>
    </Card>
  );
};

export default ReviewSelectionBlock;
